import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { createTask } from "@/modules/tasks/api";
import { AddTaskFormFields } from "@/modules/tasks/types";
import { TOAST_MESSAGES } from "@/modules/tasks/constants";

export const TOAST_OPTIONS = {
  position: "bottom-left" as const,
  autoClose: 3000,
} as const;

interface UseMutationsProps {
  setIsSubmittingTask: (value: boolean) => void;
  resetFormState: () => void;
  setOpen: (value: boolean) => void;
  /** Optional callback fired after a task is successfully created. */
  onSuccess?: () => void;
}

export function useAddTaskMutation({
  setIsSubmittingTask,
  resetFormState,
  setOpen,
  onSuccess,
}: UseMutationsProps) {
  const queryClient = useQueryClient();

  const mutationAddTask = useMutation({
    mutationFn: async (data: AddTaskFormFields) => {
      const taskData = {
        title: data.title,
        type: data.type ? data.type : undefined,
        userUId: data.userUId,
        dueDate: data.dueDate, // Use the combined date+time from form
        linkedEntities: data.linkedEntities || [],
        taskUId: data?.taskUId,
        creationSource: "CRM",
      };

      return await createTask(taskData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks-count"] });
      queryClient.invalidateQueries({ queryKey: ["myPendingTasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-statistics"] });
      queryClient.invalidateQueries({ queryKey: ["tasks-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["lead-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["track-tasks"] });

      setIsSubmittingTask(false);
      resetFormState();
      setOpen(false);
      onSuccess?.();

      showToast({
        type: ToastType.SUCCESS,
        message: TOAST_MESSAGES.ADD_TASK_SUCCESS,
        toastId: TOAST_IDS.ADD_TASK_SUCCESS,
        options: TOAST_OPTIONS,
      });
    },
    onError: () => {
      setIsSubmittingTask(false);
      showToast({
        type: ToastType.ERROR,
        message: TOAST_MESSAGES.ADD_TASK_ERROR,
        toastId: TOAST_IDS.ADD_TASK_ERROR,
        options: TOAST_OPTIONS,
      });
    },
  });

  return {
    mutationAddTask,
    isLoading: mutationAddTask.isLoading || mutationAddTask.isPending,
  };
}
